import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getDashboardStats, getCalendarEvents, getScheduleDetail, getWorkersDropdown, getDashboardVisibleUsers, updateSchedule, setScheduleCompleted, deleteSchedule, duplicateScheduleForAssignees, getLogoBase64, createMiscSchedule, getQuotes, getJobs } from '../services/api';
import {
  Users, FileText, Briefcase, Receipt, Calendar,
  X, MapPin, Phone, User, Clock, ExternalLink, Loader2,
  TrendingUp, Eye, Hammer, ClipboardList, ArrowRight, RefreshCw, UserPlus, Trash2,
  Printer, Filter, Plus, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { DatePicker, TimePicker } from '../components/DateTimePicker';
import { getPerthToday } from '../utils/dateUtils';
import { SearchableSelect } from '../components/SearchableSelect';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';

const typeLabels = { 0: 'Site Visit', 1: 'Job', 2: 'Misc Item', 3: 'Calendar Misc' };
const typeColors = { 0: '#4dbd74', 1: '#20a8d8', 2: '#F8485E', 3: '#664E88' };
const typeIcons = { 0: Eye, 1: Hammer, 2: ClipboardList, 3: Calendar };
const jobStatusLabels = { 0: 'To Book', 1: 'Booked', 2: 'Ongoing', 3: 'Completed' };
const quoteStatusLabels = { 0: 'Draft', 1: 'Assigned', 2: 'Quoted', 3: 'Follow Up', 4: 'Accepted', 5: 'Job Booked', 6: 'Cancelled' };

/* ── Stat Card ─────────────────────────────── */
function StatCard({ icon: Icon, label, value, sub, gradient, to }) {
  const inner = (
    <div className="relative overflow-hidden rounded-xl border bg-white group hover:shadow-lg transition-all duration-300" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`absolute inset-0 opacity-[0.07] ${gradient}`} />
      <div className="relative p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${gradient} shadow-sm`}>
            <Icon size={20} className="text-white" />
          </div>
          <TrendingUp size={16} className="text-slate-300 group-hover:text-slate-400 transition-colors" />
        </div>
        <div className="text-3xl font-extrabold text-slate-800 tracking-tight">{value}</div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
        {sub && <div className="mt-2 text-xs text-slate-400">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

/* ── Schedule Detail Modal ─────────────────── */
function ScheduleDetailModal({ isOpen, onClose, scheduleId, onRefresh }) {
  const [mode, setMode] = useState(null); // null | 'reschedule' | 'assignee'
  const [saving, setSaving] = useState(false);
  const [formDate, setFormDate] = useState('');
  const [formDateEnd, setFormDateEnd] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formRemarks, setFormRemarks] = useState('');
  const [formAssigneeIds, setFormAssigneeIds] = useState([]);

  const { data: detail, isLoading, refetch } = useQuery({
    queryKey: ['schedule-detail', scheduleId],
    queryFn: () => getScheduleDetail(scheduleId).then(r => r.data),
    enabled: !!scheduleId && isOpen,
  });

  const { data: workers } = useQuery({
    queryKey: ['workers-dropdown'],
    queryFn: () => getWorkersDropdown().then(r => r.data),
    enabled: isOpen && (mode === 'reschedule' || mode === 'assignee'),
  });

  // Reset mode on close or schedule change
  React.useEffect(() => { setMode(null); }, [scheduleId, isOpen]);

  const openReschedule = useCallback(() => {
    setFormDate(detail?.date || '');
    setFormDateEnd(detail?.date_end || detail?.date || '');
    setFormStart(detail?.time_start || '08:00');
    setFormEnd(detail?.time_end || '17:00');
    setFormRemarks(detail?.description || '');
    setMode('reschedule');
  }, [detail]);

  const openAssignee = () => {
    // Pre-select the current assignee so they aren't accidentally dropped.
    setFormAssigneeIds(detail?.assignee_id ? [detail.assignee_id] : []);
    setMode('assignee');
  };

  const toggleAssigneePick = (id) => {
    setFormAssigneeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSaveAssignee = async () => {
    if (!formAssigneeIds.length) { toast.error('Select at least one assignee'); return; }
    setSaving(true);
    try {
      const currentAssignee = detail?.assignee_id || '';
      // The first picked person replaces (or keeps) the assignee on THIS schedule row.
      const primary = formAssigneeIds[0];
      const additional = formAssigneeIds.slice(1);
      if (primary !== currentAssignee) {
        await updateSchedule(scheduleId, { assignee_id: primary });
      }
      let addedCount = 0;
      if (additional.length) {
        const res = await duplicateScheduleForAssignees(scheduleId, additional);
        addedCount = res.data?.count || 0;
      }
      if (addedCount > 0) {
        toast.success(`Updated assignee${addedCount > 0 ? ` and added ${addedCount} more` : ''}`);
      } else {
        toast.success('Assignee updated');
      }
      setMode(null);
      refetch();
      onRefresh?.();
    } catch { toast.error('Failed to update assignees'); }
    setSaving(false);
  };

  const handleSaveReschedule = async () => {
    if (!formDate) { toast.error('Start date is required'); return; }
    setSaving(true);
    try {
      await updateSchedule(scheduleId, { date: formDate, date_end: formDateEnd || formDate, time_start: formStart, time_end: formEnd, description: formRemarks });
      toast.success('Schedule updated');
      setMode(null);
      refetch();
      onRefresh?.();
    } catch { toast.error('Failed to update schedule'); }
    setSaving(false);
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove this schedule?')) return;
    setSaving(true);
    try {
      await deleteSchedule(scheduleId);
      toast.success('Schedule removed');
      onClose();
      onRefresh?.();
    } catch { toast.error('Failed to remove schedule'); }
    setSaving(false);
  };

  const handleToggleComplete = async () => {
    const next = !detail?.completed;
    setSaving(true);
    try {
      await setScheduleCompleted(scheduleId, next);
      toast.success(next ? 'Marked as completed' : 'Marked as not completed');
      refetch();
      onRefresh?.();
    } catch { toast.error('Failed to update completion'); }
    setSaving(false);
  };

  if (!isOpen) return null;
  const stype = detail?.schedule_type;
  const statusLabel = stype === 0 ? quoteStatusLabels[detail?.status] : jobStatusLabels[detail?.status];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl" data-testid="schedule-detail-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-lg">Schedule Details</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-5">
          {isLoading ? (
            <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" size={24} /><p className="text-sm text-slate-500 mt-2">Loading...</p></div>
          ) : detail ? (
            <>
              {/* Info + Actions row */}
              <div className="flex gap-6">
                {/* Left: Info Table */}
                <div className="flex-1">
                  <table className="w-full text-sm">
                    <tbody>
                      {detail.quote_number && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700 w-32">Quote No.</td>
                          <td className="py-2.5 text-slate-600">{detail.quote_number}</td>
                        </tr>
                      )}
                      {detail.job_number && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700 w-32">Job No.</td>
                          <td className="py-2.5 text-slate-600">{detail.job_number}</td>
                        </tr>
                      )}
                      {detail.job_title && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Job Title</td>
                          <td className="py-2.5 text-slate-600">{detail.job_title}</td>
                        </tr>
                      )}
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-700">Start Date</td>
                        <td className="py-2.5 text-slate-600">{detail.date ? new Date(detail.date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-700">Start Time</td>
                        <td className="py-2.5 text-slate-600">{detail.time_start || ''}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-700">End Date</td>
                        <td className="py-2.5 text-slate-600">{(detail.date_end || detail.date) ? new Date((detail.date_end || detail.date) + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2.5 pr-4 font-semibold text-slate-700">End Time</td>
                        <td className="py-2.5 text-slate-600">{detail.time_end || ''}</td>
                      </tr>
                      {detail.assignee_name && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Assigned To</td>
                          <td className="py-2.5 text-slate-600">{detail.assignee_name}</td>
                        </tr>
                      )}
                      {detail.client_name && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Client</td>
                          <td className="py-2.5 text-slate-600">{detail.client_name}</td>
                        </tr>
                      )}
                      {detail.address && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Address</td>
                          <td className="py-2.5 text-slate-600">{detail.address}</td>
                        </tr>
                      )}
                      {detail.client_phone && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Contact No.</td>
                          <td className="py-2.5 text-slate-600">{detail.client_phone}</td>
                        </tr>
                      )}
                      {detail.tenant_name && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Tenant</td>
                          <td className="py-2.5 text-slate-600">{detail.tenant_name}</td>
                        </tr>
                      )}
                      {detail.tenant_phone && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Contact No.</td>
                          <td className="py-2.5 text-slate-600">{detail.tenant_phone}</td>
                        </tr>
                      )}
                      {statusLabel && (
                        <tr className="border-b border-slate-100">
                          <td className="py-2.5 pr-4 font-semibold text-slate-700">Status</td>
                          <td className="py-2.5">
                            <span className="px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">{statusLabel}</span>
                          </td>
                        </tr>
                      )}
                      {detail.description && (
                        <tr>
                          <td className="py-2.5 pr-4 font-semibold text-slate-700 align-top">Remarks</td>
                          <td className="py-2.5 text-slate-600">{detail.description}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="py-2.5 pr-4 font-semibold text-slate-700 align-top">Completion</td>
                        <td className="py-2.5">
                          {detail.completed ? (
                            <span className="inline-flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 w-fit" data-testid="completion-badge">
                                <CheckCircle2 size={13} /> Completed
                              </span>
                              {detail.completed_by_name && (
                                <span className="text-[11px] text-slate-500">
                                  by {detail.completed_by_name}
                                  {detail.completed_at && ` · ${new Date(detail.completed_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">Not completed</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Right: Action Buttons */}
                <div className="flex flex-col gap-2 shrink-0 w-40">
                  <button
                    onClick={handleToggleComplete}
                    disabled={saving}
                    data-testid="toggle-complete-btn"
                    className={`px-3 py-2 text-sm text-center rounded font-medium flex items-center justify-center gap-1.5 disabled:opacity-60 ${
                      detail.completed
                        ? 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-300'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <CheckCircle2 size={14} /> {detail.completed ? 'Mark Not Done' : 'Mark Completed'}
                  </button>
                  <button onClick={openReschedule} data-testid="reschedule-btn" className="px-3 py-2 text-sm text-center rounded font-medium bg-orange-500 text-white hover:bg-orange-600 flex items-center justify-center gap-1.5">
                    <RefreshCw size={14} /> Re-Schedule
                  </button>
                  <button onClick={openAssignee} data-testid="edit-assignee-btn" className="px-3 py-2 text-sm text-center rounded font-medium bg-indigo-500 text-white hover:bg-indigo-600 flex items-center justify-center gap-1.5">
                    <UserPlus size={14} /> Add/Edit Assignee
                  </button>
                  {detail.quote_id && (
                    <Link to={`/quotes/${detail.quote_id}`} onClick={onClose} className="px-3 py-2 text-sm text-center rounded font-medium bg-sky-500 text-white hover:bg-sky-600" data-testid="view-quote-btn">
                      View quote details
                    </Link>
                  )}
                  {detail.job_id && (
                    <Link to={`/jobs/${detail.job_id}`} onClick={onClose} className="px-3 py-2 text-sm text-center rounded font-medium bg-sky-500 text-white hover:bg-sky-600" data-testid="view-job-btn">
                      View job details
                    </Link>
                  )}
                  {detail.address && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detail.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 text-sm text-center rounded font-medium bg-emerald-500 text-white hover:bg-emerald-600"
                      data-testid="view-location-btn"
                    >
                      View Location
                    </a>
                  )}
                  <button onClick={handleRemove} data-testid="remove-schedule-btn" className="px-3 py-2 text-sm text-center rounded font-medium bg-red-500 text-white hover:bg-red-600 flex items-center justify-center gap-1.5">
                    <Trash2 size={14} /> Remove Schedule
                  </button>
                </div>
              </div>

              {/* Reschedule Form */}
              {mode === 'reschedule' && (
                <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="reschedule-form">
                  <h3 className="font-semibold text-sm mb-3">Re-Schedule</h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Start Date *</label>
                      <DatePicker value={formDate} onChange={v => setFormDate(v)} testId="reschedule-date" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Start Time</label>
                      <TimePicker value={formStart} onChange={v => setFormStart(v)} testId="reschedule-start" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">End Date</label>
                      <DatePicker value={formDateEnd} onChange={v => setFormDateEnd(v)} testId="reschedule-date-end" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">End Time</label>
                      <TimePicker value={formEnd} onChange={v => setFormEnd(v)} testId="reschedule-end" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Remarks</label>
                    <input type="text" value={formRemarks} onChange={e => setFormRemarks(e.target.value)} className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm" placeholder="Optional remarks" data-testid="reschedule-remarks" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveReschedule} disabled={saving} className="px-4 py-1.5 text-sm bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50" data-testid="save-reschedule-btn">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setMode(null)} className="px-4 py-1.5 text-sm bg-slate-200 rounded hover:bg-slate-300">Cancel</button>
                  </div>
                </div>
              )}

              {/* Add / Edit Assignees — multi-select */}
              {mode === 'assignee' && (
                <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="assignee-form">
                  <h3 className="font-semibold text-sm mb-3">Add / Edit Assignees</h3>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Assign To *</label>
                    <div className="max-h-64 overflow-y-auto border border-slate-300 rounded bg-white">
                      {(!workers || workers.length === 0) && (
                        <div className="px-3 py-2 text-sm text-slate-400">No staff available</div>
                      )}
                      {workers?.map(w => {
                        const selected = formAssigneeIds.includes(w.id);
                        const isCurrent = w.id === detail?.assignee_id;
                        return (
                          <label
                            key={w.id}
                            className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${selected ? 'bg-indigo-50' : ''}`}
                            data-testid={`assignee-option-${w.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleAssigneePick(w.id)}
                              className="accent-indigo-500"
                            />
                            <span>{w.name}</span>
                            {isCurrent && (
                              <span className="ml-auto text-[10px] uppercase font-semibold text-indigo-600">Current</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveAssignee} disabled={saving} className="px-4 py-1.5 text-sm bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50" data-testid="save-assignee-btn">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setMode(null)} className="px-4 py-1.5 text-sm bg-slate-200 rounded hover:bg-slate-300">Cancel</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">No details available.</p>
          )}
        </div>
        <div className="p-4 border-t text-right">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg" data-testid="close-schedule-detail">Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Custom Event Render ───────────────────── */
function renderEventContent(eventInfo) {
  const props = eventInfo.event.extendedProps || {};
  const stype = props.schedule_type;
  const TypeIcon = typeIcons[stype] || Calendar;
  const isTimeGrid = eventInfo.view.type.includes('timeGrid');
  // `description` on schedules doubles as remarks (e.g. "ANZAC DAY" on MISC entries).
  // Show it on the tile so users don't need to open each entry to see remarks.
  const remarks = (props.description || '').trim();

  if (isTimeGrid) {
    return (
      <div className="flex flex-col gap-0.5 px-1 py-0.5 text-[10px] leading-tight overflow-hidden h-full">
        <div className="flex items-center gap-1 font-bold truncate">
          {props.completed && <CheckCircle2 size={16} className="shrink-0 text-emerald-600 fill-white drop-shadow" data-testid="event-complete-tick" />}
          <TypeIcon size={10} className="shrink-0" />
          <span className="truncate">{props.assignee_name || ''}</span>
        </div>
        {props.address && <span className="opacity-80 truncate" title={props.address}>{props.address}</span>}
        {remarks && (
          <span className="italic opacity-90 truncate" title={remarks} data-testid="event-remarks">{remarks}</span>
        )}
      </div>
    );
  }

  // Month / list view — compact pill
  return (
    <div className="flex items-center gap-1 px-1 text-[10px] leading-snug overflow-hidden">
      {props.completed && <CheckCircle2 size={15} className="shrink-0 text-emerald-600 fill-white drop-shadow" data-testid="event-complete-tick" />}
      <TypeIcon size={10} className="shrink-0" />
      <span className="font-semibold truncate">{props.assignee_name || ''}</span>
      {props.address && (
        <span className="opacity-70 truncate" title={props.address}>- {props.address}</span>
      )}
      {remarks && (
        <span className="italic opacity-80 truncate" title={remarks} data-testid="event-remarks"> · {remarks}</span>
      )}
    </div>
  );
}

/* ── Today's Agenda Sidebar ────────────────── */
function TodayAgenda({ events, onEventClick }) {
  const today = getPerthToday();
  const todayEvents = useMemo(() => {
    // Parse "...THH:MM:00" to minutes so an unpadded "8:00" sorts before "10:00".
    const toKey = (s) => {
      const m = /T(\d{1,2}):(\d{2})/.exec(s || '');
      return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 0;
    };
    return (events || [])
      // Interval overlap so a multi-day job that spans today also shows,
      // not just events whose start date === today.
      .filter(e => {
        const start = e.extendedProps?.date || e.start?.split('T')[0] || '';
        const end = e.extendedProps?.date_end || start;
        return start && start <= today && end >= today;
      })
      .sort((a, b) => toKey(a.start) - toKey(b.start));
  }, [events, today]);

  return (
    <div className="bg-white rounded-xl border h-full" data-testid="today-agenda">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <Calendar size={16} className="text-primary" />
        <h3 className="font-semibold text-sm">Today&apos;s Agenda</h3>
        <span className="ml-auto text-xs bg-primary/10 text-primary font-bold px-2 py-0.5 rounded-full">{todayEvents.length}</span>
      </div>
      <div className="divide-y max-h-[540px] overflow-y-auto">
        {todayEvents.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">
            <Calendar size={32} className="mx-auto mb-2 opacity-30" />
            No events today
          </div>
        ) : todayEvents.map((evt) => {
          const props = evt.extendedProps || {};
          const stype = props.schedule_type;
          const TypeIcon = typeIcons[stype] || Calendar;
          const time = `${props.time_start || ''} - ${props.time_end || ''}`;
          return (
            <button
              key={evt.id}
              onClick={() => onEventClick(evt.id)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors group"
              data-testid={`agenda-event-${evt.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: evt.color + '20' }}>
                  <TypeIcon size={14} style={{ color: evt.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: evt.color }} />
                    <span className="text-xs font-bold text-slate-700 truncate">{props.assignee_name || typeLabels[stype]}</span>
                    {props.completed && <CheckCircle2 size={18} className="shrink-0 text-emerald-600 fill-white drop-shadow" data-testid={`agenda-complete-tick-${evt.id}`} />}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{props.quote_number || props.job_number || props.description || ''}</div>
                  {props.address && <div className="text-[10px] text-slate-400 truncate flex items-center gap-0.5"><MapPin size={8} />{props.address}</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-0.5"><Clock size={8} />{time}</div>
                </div>
                <ArrowRight size={12} className="text-slate-300 group-hover:text-slate-500 mt-2 shrink-0 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Main Dashboard ────────────────────────── */
export default function Dashboard() {
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [showMiscModal, setShowMiscModal] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => getDashboardStats().then(r => r.data),
  });

  const { data: events = [], refetch: refetchEvents } = useQuery({
    queryKey: ['calendar-events', dateRange.start, dateRange.end],
    queryFn: () => getCalendarEvents({ start: dateRange.start, end: dateRange.end }).then(r => r.data),
    enabled: !!dateRange.start,
    staleTime: 60_000, // cache each day-range for 60s so back-forth navigation doesn't refire
  });

  // Today's Agenda has its OWN query (Perth "today"), independent of the
  // calendar's currently-viewed range. Without this the agenda went blank
  // whenever the calendar was navigated away from today or datesSet hadn't
  // fired yet (intermittent empty agenda, esp. on slower mobile/iPad loads).
  const perthToday = getPerthToday();
  const { data: todaysRawEvents = [], refetch: refetchTodayEvents } = useQuery({
    queryKey: ['calendar-events-today', perthToday],
    queryFn: () => getCalendarEvents({ start: perthToday, end: perthToday }).then(r => r.data),
    staleTime: 60_000,
  });

  const { data: workers = [] } = useQuery({
    queryKey: ['workers-dropdown'],
    queryFn: () => getWorkersDropdown().then(r => r.data),
  });

  const { data: dashboardUsers = [] } = useQuery({
    queryKey: ['dashboard-visible-users'],
    queryFn: () => getDashboardVisibleUsers().then(r => r.data),
  });

  const coloredDashboardUsers = useMemo(
    () => dashboardUsers.filter(w => w.color_code),
    [dashboardUsers]
  );

  // Apply filters to events
  const filteredEvents = useMemo(() => {
    let filtered = events;
    if (filterAssignee) {
      filtered = filtered.filter(e => e.extendedProps?.assignee_id === filterAssignee);
    }
    // Proper interval overlap: an event with [date, date_end] should be
    // included if it overlaps the filter window [filterDateStart,
    // filterDateEnd] — NOT just if its start date is inside the window.
    // Otherwise a multi-day job that started yesterday would disappear from a
    // "today only" filter even though it's still happening today.
    if (filterDateStart || filterDateEnd) {
      filtered = filtered.filter(e => {
        const start = (e.extendedProps?.date || e.start?.split('T')[0] || '');
        const end = (e.extendedProps?.date_end || start);
        if (filterDateStart && end && end < filterDateStart) return false;
        if (filterDateEnd && start && start > filterDateEnd) return false;
        return true;
      });
    }
    return filtered;
  }, [events, filterAssignee, filterDateStart, filterDateEnd]);

  const hasFilters = filterAssignee || filterDateStart || filterDateEnd;

  // Agenda respects the assignee filter but ignores the date filter (it's
  // always "today"). Sourced from the dedicated today's-events query.
  const agendaEvents = useMemo(() => {
    if (!filterAssignee) return todaysRawEvents;
    return todaysRawEvents.filter(e => e.extendedProps?.assignee_id === filterAssignee);
  }, [todaysRawEvents, filterAssignee]);

  const clearFilters = () => {
    setFilterAssignee('');
    setFilterDateStart('');
    setFilterDateEnd('');
  };

  // Print schedule in a new window
  const handlePrintSchedule = async () => {
    const dataToprint = filteredEvents.length ? filteredEvents : events;
    if (!dataToprint.length) { toast.error('No events to print'); return; }

    // Fetch logo
    let logoSrc = '';
    try {
      const logoRes = await getLogoBase64();
      logoSrc = logoRes.data.data_uri;
    } catch (e) { console.error('Logo load failed:', e); /* logo optional */ }

    const assigneeName = filterAssignee ? (workers.find(w => w.id === filterAssignee)?.name || 'All Staff') : 'All Staff';
    const dateLabel = filterDateStart || filterDateEnd
      ? `${filterDateStart ? new Date(filterDateStart + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '...'} to ${filterDateEnd ? new Date(filterDateEnd + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '...'}`
      : 'Current Calendar View';

    // Group events by assignee
    const grouped = {};
    dataToprint.forEach(e => {
      const p = e.extendedProps || {};
      const name = p.assignee_name || 'Unassigned';
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push(e);
    });
    // Sort each assignee's events chronologically: by (start date, start time, end time)
    // so a morning job always comes before an afternoon one on the same day.
    // Times are parsed to minutes (not compared as strings) so an unpadded
    // "8:00" still sorts before "10:00" instead of after it.
    const toMinutes = (t) => {
      const m = /^(\d{1,2}):(\d{2})/.exec((t || '').trim());
      return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 0;
    };
    Object.values(grouped).forEach(arr => arr.sort((a, b) => {
      const ad = (a.extendedProps?.date || '');
      const bd = (b.extendedProps?.date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      const at = toMinutes(a.extendedProps?.time_start);
      const bt = toMinutes(b.extendedProps?.time_start);
      if (at !== bt) return at - bt;
      return toMinutes(a.extendedProps?.time_end) - toMinutes(b.extendedProps?.time_end);
    }));

    let groupHtml = '';
    const sLabels = { 0: 'Draft', 1: 'Assigned', 2: 'Quoted', 3: 'Follow Up', 4: 'Accepted', 5: 'Job Booked', 6: 'Cancelled' };
    const jLabels = { 0: 'To Book', 1: 'Booked', 2: 'Ongoing', 3: 'Completed' };

    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).forEach(([name, items]) => {
      const rows = items.map(e => {
        const p = e.extendedProps || {};
        const stype = p.schedule_type;
        const typeLabel = stype === 0 ? 'Site Visit' : stype === 1 ? 'Job' : 'Misc';
        const ref = p.quote_number || p.job_number || '-';
        // Clamp the event's date span to the active filter window so a
        // multi-day job filtered to "today" doesn't print as "21 - 25 May".
        // We display ONLY the portion of the event that falls inside
        // [filterDateStart, filterDateEnd].
        const rawStart = p.date || '';
        const rawEnd = (p.date_end && p.date_end !== p.date) ? p.date_end : '';
        const effStart = filterDateStart && rawStart < filterDateStart ? filterDateStart : rawStart;
        const effEnd = filterDateEnd && rawEnd && rawEnd > filterDateEnd ? filterDateEnd : rawEnd;
        const startDate = effStart ? new Date(effStart + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '';
        const endDate = (effEnd && effEnd !== effStart) ? new Date(effEnd + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '';
        const dateDisplay = endDate ? `${startDate} - ${endDate}` : startDate;
        const time = `${p.time_start || ''} - ${p.time_end || ''}`;
        const status = stype === 0 ? (sLabels[p.status] || '') : stype === 1 ? (jLabels[p.status] || '') : '';
        const rawRemarks = (p.description || '').trim();
        const hasRemarks = rawRemarks && rawRemarks !== '-';
        const escapedRemarks = hasRemarks
          ? rawRemarks
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/\n/g, '<br/>')
          : '';
        const mainRow = `<tr class="event-row">
          <td>${typeLabel}</td>
          <td class="ref-cell">${ref}</td>
          <td>${p.client_name || '-'}</td>
          <td>${p.address || '-'}</td>
          <td class="nowrap">${dateDisplay}</td>
          <td class="nowrap">${time}</td>
          <td>${status ? `<span class="status-badge">${status}</span>` : '-'}</td>
        </tr>`;
        const remarksRow = hasRemarks
          ? `<tr class="remarks-row"><td></td><td colspan="6" class="remarks-cell"><span class="remarks-label">Remarks:</span> ${escapedRemarks}</td></tr>`
          : '';
        return mainRow + remarksRow;
      }).join('');

      groupHtml += `
        <div class="assignee-group">
          <div class="assignee-header">${name} <span class="event-count">${items.length} event${items.length > 1 ? 's' : ''}</span></div>
          <table>
            <colgroup>
              <col style="width:8%" />
              <col style="width:9%" />
              <col style="width:18%" />
              <col style="width:24%" />
              <col style="width:14%" />
              <col style="width:14%" />
              <col style="width:13%" />
            </colgroup>
            <thead><tr><th>Type</th><th>Ref</th><th>Client</th><th>Address</th><th>Date</th><th>Time</th><th>Status</th></tr></thead>
          <tbody>${rows}</tbody></table>
        </div>`;
    });

    const totalEvents = dataToprint.length;
    const printDate = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Schedule Report - Project Maintenance Perth</title>
    <style>
      @page { size: A4; margin: 8mm 8mm 10mm 8mm; }
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif;font-size:13px;color:#1e293b;line-height:1.45}
      .page{padding:12px 14px}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #e97a20;padding-bottom:14px;margin-bottom:18px}
      .header-left h1{font-size:24px;color:#1e293b;margin-bottom:3px;letter-spacing:-0.2px}
      .header-left .subtitle{font-size:12px;color:#64748b}
      .header-right{text-align:right}
      .header-right img{width:190px;height:auto;margin-bottom:4px}
      .header-right .company-info{font-size:11px;color:#64748b;line-height:1.55}
      .summary-bar{display:flex;gap:32px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px 18px;margin-bottom:20px}
      .summary-item .label{color:#64748b;font-size:11px;text-transform:uppercase;letter-spacing:.6px;font-weight:600}
      .summary-item .value{font-weight:700;color:#1e293b;margin-top:3px;font-size:14px}
      .assignee-group{margin-bottom:20px;break-inside:avoid;page-break-inside:avoid}
      .assignee-header{background:#1e293b;color:#fff;padding:9px 14px;font-size:15px;font-weight:700;border-radius:5px 5px 0 0;letter-spacing:.2px}
      .event-count{font-weight:500;font-size:12px;color:#cbd5e1;margin-left:10px}
      table{width:100%;border-collapse:collapse;table-layout:fixed;border:1px solid #e2e8f0;border-top:none}
      th{background:#f1f5f9;color:#475569;padding:9px 10px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.4px;border-bottom:2px solid #e2e8f0;font-weight:700}
      td{padding:9px 10px;font-size:13px;vertical-align:top;word-wrap:break-word;overflow-wrap:break-word}
      .event-row td{border-bottom:1px solid #eef2f6}
      .event-row:last-child td{border-bottom:none}
      .event-row:nth-of-type(odd) td{background:#fafbfc}
      .remarks-row td{border-bottom:1px solid #eef2f6;background:#fdf6ee;padding:8px 14px 10px;font-size:12.5px;color:#475569;line-height:1.55}
      .remarks-row:last-child td{border-bottom:none}
      .remarks-label{font-weight:700;color:#92400e;text-transform:uppercase;font-size:10.5px;letter-spacing:.5px;margin-right:6px}
      .ref-cell{font-weight:600;color:#0f172a;font-variant-numeric:tabular-nums}
      .nowrap{white-space:nowrap}
      .status-badge{display:inline-block;padding:3px 10px;border-radius:10px;font-size:11px;font-weight:600;background:#fef3c7;color:#92400e}
      .footer{margin-top:20px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:11px;color:#94a3b8}
      @media print{
        .page{padding:0}
        .assignee-group{break-inside:avoid;page-break-inside:avoid}
        body{font-size:12px}
        td{font-size:12px;padding:7px 8px}
        th{font-size:10px;padding:6px 8px}
        .header-left h1{font-size:22px}
        .remarks-row td{font-size:11.5px;padding:6px 10px 8px}
      }
    </style></head><body><div class="page">
      <div class="header">
        <div class="header-left"><h1>Schedule Report</h1><div class="subtitle">Internal Use Only</div></div>
        <div class="header-right">
          ${logoSrc ? `<img src="${logoSrc}" alt="Logo" />` : '<div style="font-size:16px;font-weight:bold;">Project Maintenance Perth</div>'}
          <div class="company-info">Phone: 08 6204 8116 | Mobile: 0412 627 697<br/>ABN: 69 516 939 208<br/>info@projectmaintenanceperth.com</div>
        </div>
      </div>
      <div class="summary-bar">
        <div class="summary-item"><div class="label">Assignee</div><div class="value">${assigneeName}</div></div>
        <div class="summary-item"><div class="label">Date Range</div><div class="value">${dateLabel}</div></div>
        <div class="summary-item"><div class="label">Total Events</div><div class="value">${totalEvents}</div></div>
      </div>
      ${groupHtml}
      <div class="footer"><div>Project Maintenance Perth - Schedule Report</div><div>Printed: ${printDate}</div></div>
    </div></body></html>`;

    const sanitizedHtml = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'meta', 'title'] });
    // Use a blob URL with explicit UTF-8 charset so en-dashes / quotes / non-ASCII
    // remarks don't get misinterpreted as Windows-1252 (which produced "â€"" gibberish).
    const blob = new Blob([sanitizedHtml], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    const w = window.open(blobUrl, '_blank');
    if (w) {
      w.onload = () => {
        w.print();
        // Revoke after print dialog is shown to free memory.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
      };
    } else {
      // Popup blocked — fall back to same-tab navigation.
      URL.revokeObjectURL(blobUrl);
      toast.error('Please allow pop-ups to print the schedule');
    }
  };

  const handleEventClick = useCallback((info) => {
    const schedId = info.event?.extendedProps?.schedule_id || info.event?.id || info;
    setSelectedScheduleId(schedId);
    setShowDetail(true);
  }, []);

  const handleDatesSet = useCallback((dateInfo) => {
    const startDay = dateInfo.startStr?.split('T')[0] || dateInfo.startStr;
    // FullCalendar's end is EXCLUSIVE — it points to the start of the day AFTER
    // the last visible day (e.g. Day view of "today" returns end = tomorrow).
    // Subtract one day so our inclusive backend query / print only cover the
    // days actually on screen and don't leak in the next day's events.
    let endDay = dateInfo.endStr?.split('T')[0] || dateInfo.endStr;
    if (endDay && /^\d{4}-\d{2}-\d{2}$/.test(endDay)) {
      // Parse & subtract in UTC so we never shift across a day boundary in
      // positive-offset timezones (a local-time Date.toISOString() would turn
      // a "-1 day" into "-2 days" for e.g. Perth UTC+8).
      const d = new Date(endDay + 'T00:00:00Z');
      d.setUTCDate(d.getUTCDate() - 1);
      endDay = d.toISOString().split('T')[0];
    }
    setDateRange({ start: startDay, end: endDay });
  }, []);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Overview of your operations</p>
        </div>
        {/* Legend - shows user colour assignments */}
        <div className="hidden md:flex items-center gap-4 bg-white border rounded-xl px-4 py-2 overflow-x-auto">
          {coloredDashboardUsers.map(w => (
            <div key={w.id} className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: w.color_code }} />
              {w.name}
            </div>
          ))}
          {coloredDashboardUsers.length === 0 && (
            <span className="text-xs text-slate-400">Enable &apos;Show on Dashboard&apos; in User Accounts to display staff here</span>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Clients" value={stats?.clients?.total || 0} sub={`${stats?.clients?.this_month || 0} this month`} gradient="bg-gradient-to-br from-indigo-500 to-indigo-600" to="/clients" />
        <StatCard icon={FileText} label="Quotes" value={stats?.quotes?.total || 0} sub={`${stats?.quotes?.draft || 0} draft, ${stats?.quotes?.pending || 0} pending`} gradient="bg-gradient-to-br from-emerald-500 to-emerald-600" to="/quotes" />
        <StatCard icon={Briefcase} label="Job Sheets" value={stats?.jobs?.total || 0} sub={`${stats?.jobs?.ongoing || 0} ongoing, ${stats?.jobs?.to_book || 0} to book`} gradient="bg-gradient-to-br from-sky-500 to-sky-600" to="/jobs" />
        <StatCard icon={Receipt} label="Invoices" value={stats?.invoices?.total || 0} sub={`${stats?.invoices?.unpaid || 0} unpaid`} gradient="bg-gradient-to-br from-amber-500 to-amber-600" to="/invoices" />
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl border shadow-sm p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <Filter size={15} /> Filters
          </div>
          <select
            value={filterAssignee}
            onChange={e => setFilterAssignee(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded text-sm min-w-[160px]"
            data-testid="filter-assignee"
          >
            <option value="">All Assignees</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <DatePicker
            value={filterDateStart}
            onChange={v => setFilterDateStart(v)}
            placeholder="Start Date"
            testId="filter-date-start"
          />
          <DatePicker
            value={filterDateEnd}
            onChange={v => setFilterDateEnd(v)}
            placeholder="End Date"
            testId="filter-date-end"
          />
          {hasFilters && (
            <button onClick={clearFilters} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded flex items-center gap-1" data-testid="clear-filters-btn">
              <X size={14} /> Clear
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowMiscModal(true)} className="px-4 py-1.5 text-sm bg-[#f4931d] text-white rounded hover:bg-orange-600 flex items-center gap-1.5" data-testid="add-misc-btn">
              <Plus size={14} /> Add Misc Item
            </button>
            <button onClick={handlePrintSchedule} className="px-4 py-1.5 text-sm bg-slate-800 text-white rounded hover:bg-slate-900 flex items-center gap-1.5" data-testid="print-schedule-btn">
              <Printer size={14} /> Print Schedule
            </button>
          </div>
        </div>
        {hasFilters && <div className="mt-2 text-xs text-slate-500">Showing {filteredEvents.length} of {events.length} events</div>}
      </div>

      {/* Calendar + Agenda */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        {/* Calendar */}
        <div className="xl:col-span-3 bg-white rounded-xl border shadow-sm" data-testid="dashboard-calendar">
          <div className="p-4">
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView="timeGridDay"
              locale="en-au"
              headerToolbar={{
                left: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
                center: 'title',
                right: 'prev,next today'
              }}
              businessHours
              navLinks
              dayMaxEvents={3}
              allDayText=""
              events={filteredEvents}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              height="auto"
              slotMinTime="06:00:00"
              slotMaxTime="20:00:00"
              eventContent={renderEventContent}
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: true }}
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: true }}
              eventDisplay="block"
              slotEventOverlap={false}
            />
          </div>
        </div>

        {/* Today's Agenda sidebar */}
        <div className="xl:col-span-1">
          <TodayAgenda events={agendaEvents} onEventClick={(id) => { setSelectedScheduleId(id); setShowDetail(true); }} />
        </div>
      </div>

      {/* Modal */}
      <ScheduleDetailModal
        isOpen={showDetail}
        onClose={() => { setShowDetail(false); setSelectedScheduleId(null); }}
        scheduleId={selectedScheduleId}
        onRefresh={() => { refetchEvents(); refetchTodayEvents(); }}
      />

      <style>{`
        .fc { --fc-border-color: #e2e8f0; --fc-today-bg-color: #fffbeb; }
        .fc .fc-toolbar-title { font-size: 1.05rem !important; font-weight: 700; color: #1e293b; }
        .fc .fc-toolbar { gap: 8px; }
        .fc .fc-button {
          font-size: 0.75rem !important; padding: 5px 12px !important;
          border-radius: 8px !important; font-weight: 600 !important;
          text-transform: capitalize !important;
          box-shadow: none !important;
        }
        .fc .fc-button-primary {
          background-color: #f4931d !important; border-color: #f4931d !important;
        }
        .fc .fc-button-primary:not(:disabled).fc-button-active,
        .fc .fc-button-primary:not(:disabled):active {
          background-color: #d97e0f !important; border-color: #d97e0f !important;
        }
        .fc .fc-button-primary:hover { background-color: #e5860f !important; border-color: #e5860f !important; }
        .fc .fc-today-button:disabled { opacity: 0.4 !important; }
        .fc .fc-event {
          cursor: pointer; border-radius: 6px !important;
          border: none !important; padding: 1px 2px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.08);
          transition: box-shadow 0.15s, transform 0.15s;
        }
        .fc .fc-event:hover {
          box-shadow: 0 3px 8px rgba(0,0,0,0.15); transform: translateY(-1px);
        }
        .fc .fc-timegrid-event { min-height: 32px; }
        .fc .fc-timegrid-event .fc-event-main { padding: 0; }
        .fc .fc-daygrid-event { margin: 1px 2px; }
        .fc .fc-daygrid-day-number { font-size: 0.8rem; font-weight: 600; color: #64748b; }
        .fc .fc-col-header-cell-cushion { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; }
        .fc .fc-timegrid-slot-label-cushion { font-size: 0.7rem; color: #94a3b8; }
        .fc .fc-scrollgrid { border-radius: 8px; overflow: hidden; }
        .fc .fc-list-event { cursor: pointer; }
        .fc .fc-list-event:hover td { background: #f8fafc; }
        .fc .fc-daygrid-more-link { font-size: 10px; font-weight: 700; color: #f4931d; }
      `}</style>

      {/* Misc Item Modal */}
      <MiscItemModal isOpen={showMiscModal} onClose={() => setShowMiscModal(false)} workers={workers} onCreated={refetchEvents} />
    </div>
  );
}

function MiscItemModal({ isOpen, onClose, workers, onCreated }) {
  const [form, setForm] = useState({
    assignee_ids: [], connected_to: 'job', entity_id: '',
    start_date: '', end_date: '', start_time: '', end_time: '', remarks: ''
  });
  const [saving, setSaving] = useState(false);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);

  const { data: quotesData } = useQuery({
    queryKey: ['quotes-for-misc'],
    queryFn: () => getQuotes({ page_size: 500 }).then(r => r.data),
    enabled: isOpen && form.connected_to === 'quote',
  });

  const { data: jobsData } = useQuery({
    queryKey: ['jobs-for-misc'],
    queryFn: () => getJobs({ page_size: 500 }).then(r => r.data),
    enabled: isOpen && form.connected_to === 'job',
  });

  const quotes = quotesData?.items || quotesData || [];
  const jobs = jobsData?.items || jobsData || [];

  const resetForm = () => {
    setForm({ assignee_ids: [], connected_to: '', entity_id: '', start_date: '', end_date: '', start_time: '08:00', end_time: '17:00', remarks: '' });
    setAssigneePickerOpen(false);
  };

  const handleClose = () => { resetForm(); onClose(); };

  const toggleAssignee = (workerId) => {
    setForm(prev => {
      const exists = prev.assignee_ids.includes(workerId);
      return {
        ...prev,
        assignee_ids: exists
          ? prev.assignee_ids.filter(id => id !== workerId)
          : [...prev.assignee_ids, workerId],
      };
    });
  };

  const handleCreate = async () => {
    if (!form.assignee_ids.length) { toast.error('Please select at least one assignee'); return; }
    if (!form.start_date) { toast.error('Please select a start date'); return; }
    if (!form.remarks.trim()) { toast.error('Remarks are required'); return; }
    setSaving(true);
    try {
      await createMiscSchedule(form);
      toast.success(form.assignee_ids.length > 1 ? `Misc item created for ${form.assignee_ids.length} people` : 'Misc item created');
      onCreated();
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create misc item');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="misc-item-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-slate-800">Create Misc Item</h2>
          <button onClick={handleClose} className="p-1 hover:bg-slate-100 rounded" data-testid="close-misc-modal"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Assignees (multi-select) */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Assignees</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setAssigneePickerOpen(o => !o)}
                className="w-full min-h-[42px] px-3 py-2 border border-slate-300 rounded text-sm text-left flex items-center flex-wrap gap-1.5 hover:border-slate-400"
                data-testid="misc-assignee-picker"
              >
                {form.assignee_ids.length === 0 ? (
                  <span className="text-slate-400">Select one or more assignees</span>
                ) : (
                  form.assignee_ids.map(aid => {
                    const w = workers.find(x => x.id === aid);
                    if (!w) return null;
                    return (
                      <span
                        key={aid}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-medium"
                        onClick={(e) => { e.stopPropagation(); toggleAssignee(aid); }}
                        data-testid={`misc-assignee-chip-${aid}`}
                      >
                        {w.name}
                        <X size={12} className="cursor-pointer hover:text-orange-900" />
                      </span>
                    );
                  })
                )}
              </button>
              {assigneePickerOpen && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg max-h-64 overflow-y-auto">
                  {workers.length === 0 && (
                    <div className="px-3 py-2 text-sm text-slate-400">No staff available</div>
                  )}
                  {workers.map(w => {
                    const selected = form.assignee_ids.includes(w.id);
                    return (
                      <label
                        key={w.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${selected ? 'bg-orange-50' : ''}`}
                        data-testid={`misc-assignee-option-${w.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleAssignee(w.id)}
                          className="accent-[#f4931d]"
                        />
                        <span>{w.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Connected To */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Connected To <span className="text-slate-400 font-normal">(optional)</span></label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="connected_to" value="" checked={form.connected_to === ''} onChange={() => setForm({...form, connected_to: '', entity_id: ''})} className="accent-[#f4931d]" data-testid="misc-radio-none" />
                <span className="text-sm">None</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="connected_to" value="quote" checked={form.connected_to === 'quote'} onChange={() => setForm({...form, connected_to: 'quote', entity_id: ''})} className="accent-[#f4931d]" data-testid="misc-radio-quote" />
                <span className="text-sm">Quote</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="connected_to" value="job" checked={form.connected_to === 'job'} onChange={() => setForm({...form, connected_to: 'job', entity_id: ''})} className="accent-[#f4931d]" data-testid="misc-radio-job" />
                <span className="text-sm">Job</span>
              </label>
            </div>
          </div>

          {/* Entity Selection - only show if connected to quote or job */}
          {form.connected_to && (
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">
                {form.connected_to === 'quote' ? 'Quote' : 'Job Sheet'}
              </label>
              <SearchableSelect
                value={form.entity_id}
                onChange={(v) => setForm({ ...form, entity_id: v })}
                placeholder={`Select ${form.connected_to === 'quote' ? 'Quote' : 'Job Sheet'}`}
                testId="misc-entity"
                options={
                  form.connected_to === 'quote'
                    ? quotes.map(q => ({
                        value: q.id,
                        label: `${q.quote_number || ''}${q.client_name ? ' — ' + q.client_name : ''}`,
                        sublabel: q.property_address || q.address || '',
                      }))
                    : jobs.map(j => ({
                        value: j.id,
                        label: `${j.job_number || ''}${j.client_name ? ' — ' + j.client_name : ''}`,
                        sublabel: j.property_address || j.address || '',
                      }))
                }
              />
            </div>
          )}
          {/* Date Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Start Date</label>
              <DatePicker value={form.start_date} onChange={v => setForm({...form, start_date: v})} placeholder="dd/mm/yyyy" testId="misc-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">End Date</label>
              <DatePicker value={form.end_date} onChange={v => setForm({...form, end_date: v})} placeholder="dd/mm/yyyy" testId="misc-end-date" />
            </div>
          </div>

          {/* Time Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Start Time</label>
              <TimePicker value={form.start_time} onChange={v => setForm({...form, start_time: v})} testId="misc-start-time" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">End Time</label>
              <TimePicker value={form.end_time} onChange={v => setForm({...form, end_time: v})} testId="misc-end-time" />
            </div>
          </div>

          {/* Remarks - Required */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Remarks <span className="text-red-500">*</span></label>
            <textarea value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded text-sm resize-none" placeholder="Enter remarks..." data-testid="misc-remarks" />
          </div>
        </div>

        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={handleClose} className="px-4 py-2 text-sm border border-slate-300 rounded hover:bg-slate-50" data-testid="misc-cancel-btn">Cancel</button>
          <button onClick={handleCreate} disabled={saving} className="px-4 py-2 text-sm bg-[#f4931d] text-white rounded hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1.5" data-testid="misc-create-btn">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
